import React, { useEffect } from "react";

import { ThemeProvider as MUIThemeProvider } from "@mui/material";

import { getFavicon } from "../constants/localStorageKeys";
import { useAppDispatch, useAppSelector } from "../store";
import { getColorSettingsThunk } from "../store/colorSettings/thunks";
import palette, { customPalette } from "../themes/palette";
import { createCustomTheme } from "../themes/themes";
import { darkenColor } from "../utils/darkenColor";
import { updateFavicon } from "../utils/updateFavicon";

const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const dispatch = useAppDispatch();

  const { colorSettings, isRestoreSettings, isAuthUser } = useAppSelector(
    store => ({
      colorSettings: store.colorSettings?.colorSettings,
      isRestoreSettings: store.colorSettings?.isRestoreSettings,
      isAuthUser: store.authUser?.isAuthorization,
    })
  );

  if (colorSettings) {
    palette.palette.primary.main = isRestoreSettings
      ? customPalette.primary.main
      : colorSettings.primaryGeneralColor;
    palette.palette.primary.dark = isRestoreSettings
      ? customPalette.primary.dark
      : darkenColor(colorSettings.primaryGeneralColor);
    palette.palette.secondary.main = isRestoreSettings
      ? customPalette.secondary.main
      : colorSettings.secondaryGeneralColor;
    palette.palette.secondary.dark = isRestoreSettings
      ? customPalette.secondary.dark
      : darkenColor(colorSettings.secondaryGeneralColor);
    palette.palette.text.primary = colorSettings.primaryTextColor;
    palette.palette.text.secondary = colorSettings.secondaryTextColor;
    palette.palette.error.main = colorSettings.errorTextColor;
    palette.palette.background.default = colorSettings.generalBackgroundColor;
  }

  const theme = createCustomTheme(palette);

  useEffect(() => {
    document.documentElement.style.setProperty(
      "--primary-main",
      theme.palette.primary.main
    );
    document.documentElement.style.setProperty(
      "--secondary-main",
      theme.palette.secondary.main
    );
    document.documentElement.style.setProperty(
      "--secondary-main-light",
      theme.palette.secondary.light
    );
    document.documentElement.style.setProperty(
      "--primary-dark",
      theme.palette.primary.dark
    );
    document.documentElement.style.setProperty(
      "--primary-error",
      theme.palette.error.main
    );
    document.documentElement.style.setProperty(
      "--background-default",
      theme.palette.background.default
    );
  }, [theme]);

  useEffect(() => {
    if (isAuthUser) {
      dispatch(getColorSettingsThunk());
    }
  }, [isAuthUser]);

  const hrefFavicon = getFavicon();

  useEffect(() => {
    if (colorSettings?.favicon?.size || hrefFavicon) {
      updateFavicon(
        colorSettings?.favicon?.size ? colorSettings?.favicon : undefined,
        hrefFavicon
      );
    }
  }, [colorSettings?.favicon, hrefFavicon]);

  return <MUIThemeProvider theme={theme}>{children}</MUIThemeProvider>;
};

export default ThemeProvider;
